import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import apiService from "../../utils/apiService";
import { getEngineById, isPortfolioComboEngine, isTrialEngine } from "../../config/engines";
import { clearBotStatus, updateBotStatusInStore } from "./fetchBotStatus";

/**
 * Bot IDs hidden from the dashboard after refund/delete ack until the API stops listing them (async refund).
 * Session key must stay in sync with REFUND_HIDDEN_BOT_SESSION_KEY in utils/authUtils.js (logout clears it).
 */
export const REFUND_HIDDEN_SESSION_KEY = "idx_refund_hidden_bot_ids";

export function readRefundHiddenBotIdsFromSession() {
    if (typeof window === "undefined") return [];
    try {
        const raw = window.sessionStorage.getItem(REFUND_HIDDEN_SESSION_KEY);
        const arr = raw ? JSON.parse(raw) : [];
        return Array.isArray(arr) ? [...new Set(arr.map(String))] : [];
    } catch {
        return [];
    }
}

function writeRefundHiddenBotIdsToSession(ids) {
    if (typeof window === "undefined") return;
    try {
        window.sessionStorage.setItem(
            REFUND_HIDDEN_SESSION_KEY,
            JSON.stringify(ids),
        );
    } catch {
        /* quota / private mode */
    }
}

/** Refund DELETE failed but bot no longer exists — drop ghost cards without treating as error. */
function isDeleteBotGoneFromServer(message) {
    const m = String(message || "").toLowerCase();
    return (
        m.includes("not found") ||
        m.includes("404") ||
        m.includes("bot not found")
    );
}

function isCompletedTrialBot(bot) {
    const engine = bot?.engine ?? bot?.apiData?.engine;
    if (!isTrialEngine(engine)) return false;
    const trial =
        bot?.activitySummaryState?.trial ?? bot?.apiData?.trialProgress ?? null;
    return Boolean(trial?.completed);
}

export const fetchAllBots = createAsyncThunk(
    'fetchBots/fetchAllBots',
    async ({ silent = false } = {}, { rejectWithValue, dispatch }) => {
        try {
            const botsArray = await apiService.getAllBots();
            
            // Ensure we have an array of bots
            const botsToTransform = Array.isArray(botsArray) ? botsArray : [];

            const effectiveListStatus = (b) => {
                const db = b?.status || "stopped";
                // Admin pause keeps status "running"; stale flag on stopped ≠ running.
                if (b?.stopedByAdmin === true && db === "running") {
                    return "running";
                }
                if (db === "stopped" || db === "paused_by_admin") {
                    return db === "paused_by_admin" ? "paused_by_admin" : "stopped";
                }
                if (db === "starting" || db === "stopping") return db;
                if (db === "running") return "running";
                if (b?.isRunning === true) return "running";
                return db;
            };

            for (const bot of botsToTransform) {
                if (bot?.id != null) {
                    const listStatus = effectiveListStatus(bot);
                    dispatch(
                        updateBotStatusInStore({
                            botId: bot.id,
                            status: listStatus,
                            stopCode: bot.stopCode ?? null,
                            stoppedReason: bot.stoppedReason ?? null,
                            stopedByAdmin:
                                bot.stopedByAdmin === true &&
                                (bot.status || "stopped") === "running",
                        }),
                    );
                }
            }
            
            // Transform the API response data to match bot card format
            const transformedBots = botsToTransform
                .filter((bot) => !isCompletedTrialBot({ apiData: bot, engine: bot.engine }))
                .map((bot, index) => {
                // Helper function to get engine icon name (string only, not React component)
                const getEngineIconName = (engine) => {
                    const engineConfig = getEngineById(engine);
                    if (engineConfig) {
                        // Map emoji icons to Lucide icon names
                        switch (engineConfig.icon) {
                            case '🧪': return 'FlaskConical';
                            case '🚶‍♂️‍➡️': return 'Zap';
                            case '🏎️': return 'Zap';
                            case '🚀': return 'Rocket';
                            case '⚡': return 'Crown';
                            default: return 'Zap';
                        }
                    }
                    return 'Zap';
                };

                const getEngineColor = (engine) => {
                    const engineConfig = getEngineById(engine);
                    return engineConfig ? engineConfig.color : 'from-cyan-400 to-blue-500';
                };
                
                return {
                    id: bot.id || `bot-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                    name: bot.botName || `Bot ${index + 1}`,
                    status: (() => {
                        const db = bot.status || "stopped";
                        if (bot.stopedByAdmin === true && db === "running") {
                            return "running";
                        }
                        if (db === "stopped" || db === "paused_by_admin") {
                            return db === "paused_by_admin" ? "paused_by_admin" : "stopped";
                        }
                        if (db === "starting" || db === "stopping") return db;
                        if (db === "running") return "running";
                        if (bot.isRunning === true) return "running";
                        return db;
                    })(),
                    engine: bot.engine || 'V4',
                    engineIconName: getEngineIconName(bot.engine),
                    engineColor: getEngineColor(bot.engine),
                    tradingPair: bot.tradingPair || `${bot.tokenSymbol || 'IDX'}/SOL`,
                    baseToken: bot.baseToken || bot.tokenSymbol || 'IDX',
                    quoteToken: bot.quoteToken || 'SOL',
                    strategy: bot.strategy || 'Balanced',
                    riskLevel: bot.riskLevel || 'Medium',
                    createdAt: bot.createdAt ? new Date(bot.createdAt).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
                    uptime: bot.uptime || '0h 0m',
                    publicKey: bot.userWallet || bot.publicKey || 'N/A',
                    tokenAddress: bot.tokenAddress || 'N/A',
                    currentPrice: bot.currentPrice || 0.00,
                    priceChange24h: bot.priceChange24h || 0.0,
                    lastTrade: bot.lastTrade || 'Just created',
                    avgTradeSize: bot.avgTradeSize || 0,
                    dailyVolume: bot.dailyVolume || 0,
                    settings: bot.settings || {
                        maxTradeAmount: 500,
                        minGap: '1-5min',
                        gasReserve: 2,
                        autoRefund: true
                    },
                    performance: bot.performance || {
                        today: { trades: 0, profit: 0, volume: 0 },
                        yesterday: { trades: 0, profit: 0, volume: 0 },
                        week: { trades: 0, profit: 0, volume: 0 }
                    },
                    activitySummaryState: bot.trialProgress
                        ? { trial: bot.trialProgress }
                        : undefined,
                    // Store the original API response data
                    apiData: bot
                };
            });

            return transformedBots;
        }
        catch (error) {
            return rejectWithValue(error.message);
        }
    },
    {
        condition: (arg, { getState }) => {
            if (arg?.silent) return true;
            return getState().fetchBots.loading !== 'pending';
        },
    },
);

export const deleteBot = createAsyncThunk(
    'bots/deleteBot',
    async ({ botId, walletAddress }, { rejectWithValue, dispatch }) => {
        try {
            const response = await apiService.request(`/bots/${botId}/refund`, {
                method: 'POST',
                body: { user_wallet_address: walletAddress },
            });

            dispatch(clearBotStatus(String(botId)));

            // Return both the API response and botId
            return {
                botId: botId,
                response: response
            };
        }
        catch (error) {
            const apiMessage =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                error.message;
            console.error('Delete bot error:', apiMessage);
            if (isDeleteBotGoneFromServer(apiMessage)) {
                dispatch(clearBotStatus(String(botId)));
            }
            return rejectWithValue(apiMessage);
        }
    }
);



const initialState = {
    bots: [],
    loading: 'idle',
    error: null,
    deleteLoading: false,
    optimisticDeleteBackup: null,
    /** IDs removed from UI after refund POST until GET /bots no longer returns them */
    optimisticallyHiddenBotIds: [],
    /** Ignore out-of-order fetchAllBots responses (older request finishing after a newer one). */
    latestFetchBotsRequestId: null,
    lastBotsFetchAt: 0,
};

const fetchBotSlice = createSlice({
    name: 'fetchBots',
    initialState,
    reducers: {
        resetBots(state) {
            state.bots = [];
            state.loading = 'idle';
            state.error = null;
            state.deleteLoading = false;
            state.optimisticDeleteBackup = null;
            state.optimisticallyHiddenBotIds = [];
            state.latestFetchBotsRequestId = null;
            writeRefundHiddenBotIdsToSession([]);
        },
        syncRefundHiddenIdsFromSession(state) {
            state.optimisticallyHiddenBotIds = readRefundHiddenBotIdsFromSession();
        },
        hydrateBotsFromCache(state, action) {
            if (Array.isArray(action.payload)) {
                state.optimisticDeleteBackup = null;
                const hidden = new Set(
                    state.optimisticallyHiddenBotIds.map(String),
                );
                const filtered = action.payload.filter(
                    (b) =>
                        !hidden.has(String(b?.id ?? b?.apiData?.id)) &&
                        !isCompletedTrialBot(b),
                );
                state.bots = filtered;
            }
        },
        addBot(state, action) {
            state.bots.unshift(action.payload);
        },
        updateBot(state, action) {
            const { id, updates } = action.payload;
            const rid = id != null && id !== "" ? String(id) : id;
            const botIndex = state.bots.findIndex(
                (bot) => String(bot.id) === rid,
            );
            if (botIndex !== -1) {
                const prev = state.bots[botIndex];
                const { apiData: apiDataPatch, ...restUpdates } = updates;
                const next = { ...prev, ...restUpdates };
                if (apiDataPatch && typeof apiDataPatch === "object") {
                    next.apiData = { ...(prev.apiData || {}), ...apiDataPatch };
                }
                // Keep nested apiData.status aligned — otherwise stop UI can read a
                // stale "stopped" from apiData after a later start updated only top-level status.
                if (restUpdates.status != null) {
                    next.apiData = {
                        ...(next.apiData || prev.apiData || {}),
                        status: restUpdates.status,
                    };
                }
                state.bots[botIndex] = next;
            }
        },
        removeBot(state, action) {
            const rid = String(action.payload);
            state.bots = state.bots.filter(
                (bot) => String(bot.id) !== rid,
            );
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch all bots reducers
            .addCase(fetchAllBots.pending, (state, action) => {
                const silent = Boolean(action.meta.arg?.silent);
                if (!silent) {
                    state.loading = 'pending';
                    state.error = null;
                    state.latestFetchBotsRequestId = action.meta.requestId;
                    state.lastBotsFetchAt = Date.now();
                }
            })
            .addCase(fetchAllBots.fulfilled, (state, action) => {
                const silent = Boolean(action.meta.arg?.silent);
                const incoming = action.payload;
                const incomingIdSet = new Set(
                    incoming.map((b) => String(b.id)),
                );

                if (silent && state.loading === 'pending') {
                    state.optimisticallyHiddenBotIds =
                        state.optimisticallyHiddenBotIds.filter((id) =>
                            incomingIdSet.has(String(id)),
                        );
                    writeRefundHiddenBotIdsToSession(
                        state.optimisticallyHiddenBotIds,
                    );
                    return;
                }

                if (
                    !silent &&
                    state.latestFetchBotsRequestId != null &&
                    action.meta.requestId !== state.latestFetchBotsRequestId
                ) {
                    return;
                }

                if (!silent) {
                    state.loading = 'idle';
                }
                state.error = null;
                state.optimisticDeleteBackup = null;
                state.optimisticallyHiddenBotIds =
                    state.optimisticallyHiddenBotIds.filter((id) =>
                        incomingIdSet.has(String(id)),
                    );
                writeRefundHiddenBotIdsToSession(state.optimisticallyHiddenBotIds);
                const hidden = new Set(state.optimisticallyHiddenBotIds.map(String));
                state.bots = incoming.filter(
                    (b) => !hidden.has(String(b.id)),
                );
            })
            .addCase(fetchAllBots.rejected, (state, action) => {
                if (Boolean(action.meta.arg?.silent)) {
                    return;
                }
                if (
                    state.latestFetchBotsRequestId != null &&
                    action.meta.requestId !== state.latestFetchBotsRequestId
                ) {
                    return;
                }
                state.loading = 'idle';
                state.error = action.payload || action.error.message;
            })

            .addCase(deleteBot.pending, (state, action) => {
                state.deleteLoading = true;
                state.error = null;
                state.optimisticDeleteBackup = null;
            })
            .addCase(deleteBot.fulfilled, (state, action) => {
                state.deleteLoading = false;
                state.optimisticDeleteBackup = null;
                const rid = String(action.payload.botId);
                state.optimisticallyHiddenBotIds = [
                    ...new Set([...state.optimisticallyHiddenBotIds.map(String), rid]),
                ];
                writeRefundHiddenBotIdsToSession(state.optimisticallyHiddenBotIds);
                state.bots = state.bots.filter((bot) => String(bot.id) !== rid);
                state.error = null;
            })
            .addCase(deleteBot.rejected, (state, action) => {
                state.deleteLoading = false;
                state.optimisticDeleteBackup = null;
                const msg =
                    typeof action.payload === "string"
                        ? action.payload
                        : action.error?.message || "";
                const botId = action.meta?.arg?.botId;
                if (
                    botId != null &&
                    isDeleteBotGoneFromServer(
                        msg ||
                            (typeof action.payload === "string"
                                ? action.payload
                                : ""),
                    )
                ) {
                    const rid = String(botId);
                    state.bots = state.bots.filter(
                        (b) => String(b.id) !== rid,
                    );
                    state.optimisticallyHiddenBotIds =
                        state.optimisticallyHiddenBotIds.filter(
                            (id) => String(id) !== rid,
                        );
                    writeRefundHiddenBotIdsToSession(
                        state.optimisticallyHiddenBotIds,
                    );
                    state.error = null;
                } else {
                    state.error = action.payload || action.error.message;
                }
            });
    }
});

export const {
    resetBots,
    hydrateBotsFromCache,
    addBot,
    updateBot,
    removeBot,
    syncRefundHiddenIdsFromSession,
} = fetchBotSlice.actions;

export const fetchBotReducer = fetchBotSlice.reducer;
export default fetchBotReducer;